import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Chip,
} from '@mui/material';
import {
    Visibility as PreviewIcon,
    CloseRounded as CloseIcon
} from '@mui/icons-material';
import {
    Button as RButton,
    Card as RCard,
    CardHeader as RCardHeader,
    Collapse as RCollapse,
} from 'reactstrap';
import FeatherIcon from "feather-icons-react";
import moment from 'moment';
import { getMoveInOutReportsByResident } from './api';
import { PreviewMoveInMoveOutInspection } from '../../../Move-In&Move-Out/Preview/PreviewMoveInMoveOutInspection';

/** Every name the record knows the resident by; the server splits, de-blanks and normalizes. */
export const residentNamesOf = (item) => {
    if (!item) return [];
    const people = [...(item.meta?.lease_holders || []), ...(item.meta?.occupants || [])];
    const names = people.map((p) => (typeof p === 'string' ? p : p?.name)).filter(Boolean);
    const legacy = Array.isArray(item.tenants) ? item.tenants.join(', ') : item.tenants;
    if (legacy && typeof legacy === 'string') names.push(legacy);
    return names;
};

const STATUS_COLORS = { Signed: 'success', Disagreement: 'error', 'Awaiting Tenant': 'warning' };
const formatDate = (d) => (d ? moment.utc(d).format('DD-MM-YYYY') : '—');

const ReportCard = ({ report, onOpen }) => (
    <Card variant="outlined" sx={{ borderColor: '#e4e9f2', borderRadius: 2, boxShadow: 'none', '&:hover': { backgroundColor: '#fafcff', borderColor: '#c6d6e8' } }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3, py: '14px !important', '&:last-child': { pb: '14px !important' }, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 135 }}>
                <Chip size="small" color="primary" label={report.phase} />
                <Tooltip title="View report">
                    <IconButton aria-label="View report" size="small" color="primary" onClick={() => onOpen(report)} sx={{ border: '1px solid #c6d6e8', borderRadius: 1 }}>
                        <PreviewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Typography variant="body2" sx={{ minWidth: { md: 180 }, flex: 1 }}><strong>Tenant(s):</strong> {(report.tenants || []).join(', ') || '—'}</Typography>
            {report.move_in_status && (
                <Typography variant="body2" component="div" sx={{ minWidth: 175 }}>
                    <strong>Move-In:</strong> {formatDate(report.move_in_date)}{' '}
                    <Chip size="small" variant="outlined" color={STATUS_COLORS[report.move_in_status] || 'default'} label={report.move_in_status} />
                </Typography>
            )}
            {report.move_out_status && (
                <Typography variant="body2" component="div" sx={{ minWidth: 175 }}>
                    <strong>Move-Out:</strong> {formatDate(report.move_out_date)}{' '}
                    <Chip size="small" variant="outlined" color={STATUS_COLORS[report.move_out_status] || 'default'} label={report.move_out_status} />
                </Typography>
            )}
        </CardContent>
    </Card>
);

const ReportsList = ({ propertyId, unitNo, names }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selected, setSelected] = useState(null);
    const namesKey = names.join('|');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);
        getMoveInOutReportsByResident(propertyId, unitNo, names)
            .then((response) => { if (!cancelled) setReports(response?.data || []); })
            .catch(() => { if (!cancelled) { setReports([]); setError(true); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyId, unitNo, namesKey]);

    // Browser Back closes the report instead of leaving the mastersheet (same as Lease History).
    useEffect(() => {
        if (!selected) return undefined;
        window.history.pushState({ modalOpen: true }, '');
        const handlePopState = () => setSelected(null);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [selected]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
                <Typography variant="body1" ml={2}>Loading reports...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="warning" sx={{ my: 2 }}>Could not load Move-In / Move-Out reports. Try again later.</Alert>;
    }

    return (
        <Box>
            {reports.length === 0 ? (
                <Alert severity="info" sx={{ my: 2 }}>
                    No Move-In / Move-Out report matches this resident's name and unit number.
                </Alert>
            ) : (
                <Grid container spacing={2}>
                    {reports.map((report) => (
                        <Grid item xs={12} key={report._id}>
                            <ReportCard report={report} onOpen={setSelected} />
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog open={!!selected} onClose={() => setSelected(null)} fullScreen>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Tooltip title="Close">
                        <IconButton onClick={() => setSelected(null)} size="small" sx={{ color: 'red' }}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>
                <DialogContent dividers>
                    {selected && <PreviewMoveInMoveOutInspection token={selected.previewToken} />}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

const MoveInOutReports = ({ propertyId, unitNo, item }) => {
    const [isOpen, setIsOpen] = useState(false);
    const names = useMemo(() => residentNamesOf(item), [item]);
    const ready = Boolean(propertyId && unitNo && names.length);

    if (!ready) {
        return (
            <RCard className="mb-4">
                <RCardHeader className="d-flex justify-content-between align-items-center">
                    <div className="p-0">
                        Move-In / Move-Out Reports
                        <Typography variant="caption" component="div">
                            Reports appear here once the unit has a resident name and unit number to match.
                        </Typography>
                    </div>
                </RCardHeader>
            </RCard>
        );
    }

    return (
        <RCard className="mb-4">
            <RCardHeader className="py-3 text-center">
                <RButton
                    color="primary"
                    outline
                    className="fw-bold px-4 py-2 rounded-pill"
                    onClick={() => setIsOpen((prev) => !prev)}
                >
                    <FeatherIcon icon="clipboard" className="me-2" size={16} />
                    Move-In / Move-Out Reports
                </RButton>
            </RCardHeader>
            <RCollapse isOpen={isOpen}>
                <CardContent>
                    {isOpen && <ReportsList propertyId={propertyId} unitNo={unitNo} names={names} />}
                </CardContent>
            </RCollapse>
        </RCard>
    );
};

export { MoveInOutReports };
